import gql from "graphql-tag";
import { useMutation, useQuery } from "react-apollo";
import { GetV2Channel, GetV2ChannelVariables } from "./__generated__/GetV2Channel";
import Loading from "../../../Loading/Loading";
import {
    Add,
    ArrowLeft,
    Check,
    DeleteForeverOutlined,
    Dvr,
    Edit,
    InputOutlined,
    Link,
    PlayArrow,
    SmartDisplayOutlined,
    Stop,
    VideoSettingsOutlined,
} from "@mui/icons-material";
import V2ChannelEngineDetails from "./V2ChannelEngineDetails";
import { V2ChannelIngestDetails } from "./V2ChanelIngestDetails";
import V2ChannelDistributionDetails from "./V2ChannelDistributionDetails";
import { useState } from "react";
import AuthService from "../../../../auth/AuthService";
import { Alert, Link as MuiLink, Tooltip } from "@mui/material";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { GraphQLError } from "graphql";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "@reach/router";
import { DeleteV2ChannelMutation, DeleteV2ChannelMutationVariables } from "./__generated__/DeleteV2ChannelMutation";
import { GET_V2_CHANNELS_OF_ORGANIZATION } from "../V2ChannelsOverview";
import { StartV2ChannelMutation, StartV2ChannelMutationVariables } from "./__generated__/StartV2ChannelMutation";
import { StopV2ChannelMutation, StopV2ChannelMutationVariables } from "./__generated__/StopV2ChannelMutation";
import UpdateChannelV2Dialog from "./UpdateChannelV2Dialog";
import CreateV2IngestDialog from "./Ingest/CreateV2IngestDialog";
import CreateEngineDialog from "./Engine/CreateEngineDialog";
import CreateDistributionDialog from "./Distributions/CreateDistributionDialog";
import { GetOrganizationBasics_organization } from "../../../GetStarted/__generated__/GetOrganizationBasics";

interface Props {
    organization: GetOrganizationBasics_organization;
    channelId: string;
}
export const GET_V2_CHANNEL = gql`
    query GetV2Channel($organizationId: ID!, $channelId: ID!) {
        getV2Channel(organizationId: $organizationId, channelId: $channelId) {
            id
            externalId
            name
            timeout
            ads {
                enabled
                settings {
                    assetKey
                    layout
                    enableBumperAds
                    podDuration
                }
            }
            dvr {
                enabled
                windowInSeconds
            }
            nielsen {
                enabled
            }
            counts {
                engines
                ingests
            }
            ingests {
                id
                name
                type
                streamKey
                region
                url
                tracks {
                    audio {
                        pid
                        language
                        label
                    }
                }
                captions {
                    channel
                    language
                    label
                }
            }
            engines {
                id
                name
                ingestId
                region
                status
                drm
                priority
                encoderPreset
                version
                onlineSince
                daiAssetKey
                outputs {
                    hls
                    hesp
                    hlsMpegTs
                }
                quality {
                    abrLadderId
                }
                override {
                    image
                }
            }
            distributions {
                id
                name
                enabled
                targetLatency
                maxBitrate
                dvr {
                    enabled
                }
                outputs {
                    hesp
                    hls
                    hlsMpegTs
                }
                parameters {
                    key
                    value
                }
                security {
                    ipBlocking {
                        enabled
                        mode
                        cidrs
                    }
                    skipJwtChecks
                    geoBlocking {
                        enabled
                        mode
                        countries
                    }
                    refererBlocking {
                        enabled
                        allowedDomains
                        allowNativeApps
                    }
                    keys {
                        id
                        name
                    }
                }
                endpoints {
                    engineIds
                    cdns {
                        fastly {
                            weight
                        }
                        akamai {
                            weight
                        }
                    }
                }
                webRtc {
                    priority
                    src {
                        accountId
                        name
                        apiUrl
                    }
                }
            }
            regions {
                id
                name
                rtmpPushEndpoint
            }
            abrLadders {
                id
                name
            }
        }
    }
`;

export default function V2ChannelDetails({ channelId, organization }: Props) {
    const { organizationId } = organization;

    const { data, loading } = useQuery<GetV2Channel, GetV2ChannelVariables>(GET_V2_CHANNEL, {
        variables: { channelId, organizationId },
        pollInterval: 10_000,
    });

    const [highlightEngineId, setHighlightEngineId] = useState<string | undefined>();
    const [highlightIngestId, setHighlightIngestId] = useState<string | undefined>();
    const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
    const [openConfirmStart, setOpenConfirmStart] = useState<boolean>(false);
    const [openConfirmStop, setOpenConfirmStop] = useState<boolean>(false);
    const [openEdit, setOpenEdit] = useState<boolean>(false);

    const [openCreateIngest, setOpenCreateIngest] = useState<boolean>(false);
    const [openCreateEngine, setOpenCreateEngine] = useState<boolean>(false);
    const [openCreateDistribution, setOpenCreateDistribution] = useState<boolean>(false);

    const navigate = useNavigate();

    const [deleteChannel] = useMutation<DeleteV2ChannelMutation, DeleteV2ChannelMutationVariables>(DELETE_V2_CHANNEL);
    const [startChannel] = useMutation<StartV2ChannelMutation, StartV2ChannelMutationVariables>(START_V2_CHANNEL);
    const [stopChannel] = useMutation<StopV2ChannelMutation, StopV2ChannelMutationVariables>(STOP_V2_CHANNEL);

    if (loading) {
        return <Loading />;
    }

    if (!data) {
        return <div>No channel found</div>;
    }

    const channel = data.getV2Channel;

    function onHightlightEngineId(engineId: string): void {
        const connectedIngest = channel.engines.find((engine) => engine.id === engineId)?.ingestId;
        setHighlightEngineId(engineId);
        setHighlightIngestId(connectedIngest);
    }

    const isAdmin = AuthService.isTHEOAdmin();
    const hasNonStoppedEngines = channel.engines.filter((engine) => engine.status !== "stopped").length > 0;

    async function onConfirmChannelDelete() {
        try {
            await deleteChannel({
                variables: { input: { id: channel.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNELS_OF_ORGANIZATION, variables: { organizationId } }],
            });
            toast.success("Channel deleted");
            navigate(`/app/${organizationId}/channels`);
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmDelete(false);
    }

    async function onConfirmChannelStart() {
        try {
            await startChannel({
                variables: { input: { id: channel.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("All engines started");
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmStart(false);
    }

    async function onConfirmChannelStop() {
        try {
            await stopChannel({
                variables: { input: { id: channel.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("All engines stopped");
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmStop(false);
    }

    return (
        <div className="flex flex-col w-full gap-2">
            <Alert severity="info" style={{ marginBottom: 8 }}>
                V2 channel management will soon move exclusively to our new dashboard. Please visit{" "}
                <MuiLink
                    href="https://dashboard.optiview.dolby.com"
                    style={{ color: "blue", textDecoration: "none" }}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    dashboard.optiview.dolby.com
                </MuiLink>{" "}
                to manage your V2 channels. You can log in with the same credentials you use for the THEOlive console.
            </Alert>
            <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-1 items-center">
                    <div className="font-semibold text-xl">{channel.name}</div>
                    {channel.ads?.enabled === true && (
                        <div>
                            <div className="text-xs bg-green-50 rounded-md py-[2px] mt-1 px-2  text-center">
                                <Check color="inherit" style={{ fontSize: "12px" }} /> Ads (asset key:{" "}
                                <span className="font-mono">{channel.ads?.settings?.assetKey}</span>)
                            </div>
                        </div>
                    )}
                    {channel.dvr?.enabled === true && (
                        <div>
                            <div className="text-xs bg-yellow-50 rounded-md py-[2px] mt-1 px-2 items-center flex gap-2 text-center">
                                <Dvr color="inherit" style={{ fontSize: "12px" }} />{" "}
                                <div>
                                    <strong>DVR</strong> ({channel.dvr?.windowInSeconds} seconds)
                                </div>
                            </div>
                        </div>
                    )}
                    {channel.nielsen?.enabled === true && (
                        <div>
                            <div className="text-xs bg-blue-50 rounded-md py-[2px] mt-1 px-2 items-center flex gap-2 text-center">
                                <ArrowLeft color="inherit" style={{ fontSize: "12px" }} />{" "}
                                <div>
                                    <strong>Nielsen</strong>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="ml-2 flex flex-row items-center gap-2">
                        <button
                            className="bg-blue-900 hover:bg-blue-800 text-white rounded-md px-1 py-[2px] text-xs"
                            onClick={() => setOpenEdit(true)}
                        >
                            <Edit style={{ fontSize: "14px" }} />
                        </button>
                    </div>
                    <div className="ml-2 flex flex-row items-center gap-2">
                        <Tooltip title={hasNonStoppedEngines ? "Could not delete: engines running" : "Delete channel"}>
                            <button
                                disabled={hasNonStoppedEngines}
                                onClick={() => setOpenConfirmDelete(true)}
                                className="bg-red-100 hover:bg-red-200 text-black rounded-md px-1 py-[2px] text-xs disabled:bg-slate-50 disabled:text-slate-300"
                            >
                                <DeleteForeverOutlined style={{ fontSize: "14px" }} />
                            </button>
                        </Tooltip>
                    </div>
                </div>

                <div className="text-xs text-slate-500 flex flex-row items-center">
                    <div>
                        {channel.id} {channel.externalId && <span>- External ID: {channel.externalId}</span>}{" "}
                    </div>
                    {isAdmin && (
                        <div className="ml-2">
                            <button
                                className="bg-slate-200 rounded-md px-2 flex flex-row text-xs gap-2 items-center"
                                onClick={() =>
                                    window.open(
                                        `https://theolens.live/?env=${process.env
                                            .REACT_APP_THEO_ENV!}&customerId=${organizationId}`,
                                    )
                                }
                            >
                                <Link /> Lens
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full flex flex-col xl:flex-row gap-6">
                <div className="bg-white shadow-md rounded-md py-2 px-4 w-full  border-[1px] border-slate-300">
                    <div className="flex flex-row justify-between gap-2">
                        <div className="font-semibold mb-2 flex flex-row gap-2 items-center">
                            <InputOutlined className="text-orange-600" color="inherit" style={{ fontSize: "18px" }} />{" "}
                            Ingests
                        </div>
                        <div>
                            <button
                                onClick={() => setOpenCreateIngest(true)}
                                className="bg-blue-900 hover:bg-blue-800 text-white rounded-md px-1 py-1 flex flex-row items-center gap-1 text-xs disabled:text-slate-300 disabled:bg-slate-100"
                            >
                                <Add style={{ fontSize: "14px" }} /> Create
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 text-sm">
                        {channel.ingests.length === 0 && (
                            <div className="italic text-gray-500 text-sm">No ingests connected</div>
                        )}
                        {channel.ingests.map((ingest, i) => (
                            <V2ChannelIngestDetails
                                key={`ingest-${i}`}
                                ingest={ingest}
                                regions={channel.regions}
                                engines={channel.engines}
                                channelId={channel.id}
                                organizationId={organizationId}
                                highlight={highlightIngestId === ingest.id}
                            />
                        ))}
                    </div>
                </div>
                <div className="bg-white shadow-md rounded-md py-2 px-4 w-full border-[1px] border-slate-300">
                    <div className="flex flex-row justify-between gap-2">
                        <div className="font-semibold mb-2 flex flex-row gap-2 items-center">
                            <VideoSettingsOutlined
                                className="text-purple-600"
                                color="inherit"
                                style={{ fontSize: "18px" }}
                            />{" "}
                            Engines
                        </div>
                        <div className="flex flex-row gap-2">
                            <div>
                                <Tooltip
                                    title={
                                        channel.ingests.length === 0
                                            ? "Please create an ingest first before creating an engine"
                                            : "Create engine"
                                    }
                                >
                                    <button
                                        onClick={() => setOpenCreateEngine(true)}
                                        disabled={channel.ingests.length === 0}
                                        className="bg-blue-900 hover:bg-blue-800 text-white rounded-md px-1 py-1 flex flex-row items-center gap-1 text-xs disabled:text-slate-300 disabled:bg-slate-100"
                                    >
                                        <Add style={{ fontSize: "14px" }} /> Create
                                    </button>
                                </Tooltip>
                            </div>
                            <div>
                                <button
                                    disabled={
                                        channel.engines.filter((engine) => engine.status !== "stopped").length ===
                                        channel.engines.length
                                    }
                                    onClick={() => setOpenConfirmStart(true)}
                                    className="bg-green-500 hover:bg-green-400 text-white rounded-md px-1 py-1 text-xs flex flex-row items-center gap-1 disabled:text-slate-300 disabled:bg-slate-100"
                                >
                                    <PlayArrow style={{ fontSize: "14px" }} /> Start all
                                </button>
                            </div>
                            <div>
                                <button
                                    disabled={
                                        channel.engines.filter((engine) => engine.status === "stopped").length ===
                                        channel.engines.length
                                    }
                                    onClick={() => setOpenConfirmStop(true)}
                                    className="bg-red-500 hover:bg-red-400 text-white rounded-md px-1 py-1 text-xs flex flex-row items-center gap-1 disabled:text-slate-300 disabled:bg-slate-100"
                                >
                                    <Stop style={{ fontSize: "14px" }} /> Stop all
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 text-sm">
                        {channel.engines.length === 0 && (
                            <div className="italic text-gray-500 text-sm">No engines connected</div>
                        )}
                        {channel.engines
                            .sort((a, b) =>
                                a.priority < b.priority
                                    ? -1
                                    : a.priority === b.priority
                                    ? a.name < b.name
                                        ? -1
                                        : 1
                                    : 1,
                            )
                            .map((engine, i) => (
                                <V2ChannelEngineDetails
                                    key={`engine-${i}`}
                                    engine={engine}
                                    ingests={channel.ingests}
                                    channel={channel}
                                    organizationId={organizationId}
                                    highlight={highlightEngineId === engine.id}
                                    onHover={() => setHighlightIngestId(engine.ingestId)}
                                    onStopHover={() => setHighlightIngestId(undefined)}
                                />
                            ))}
                    </div>
                </div>
            </div>
            <div className="bg-white shadow-md rounded-md py-2 px-4 w-full mt-4 border-[1px] border-slate-300">
                <div className="flex flex-row gap-2 justify-between w-full">
                    <div className="font-semibold mb-2 flex flex-row gap-2 items-center">
                        <SmartDisplayOutlined
                            className="text-yellow-600"
                            color="inherit"
                            style={{ fontSize: "18px" }}
                        />{" "}
                        Distributions
                    </div>
                    <div>
                        {/* <Tooltip
                            title={
                                channel.engines.length === 0
                                    ? "Please create an engine first before creating a distribution"
                                    : "Create distribution"
                            }
                        >  */}
                        <button
                            onClick={() => setOpenCreateDistribution(true)}
                            // disabled={channel.engines.length === 0}
                            className="bg-blue-900 hover:bg-blue-800 text-white rounded-md px-1 py-1 flex flex-row items-center gap-1 text-xs disabled:text-slate-300 disabled:bg-slate-100"
                        >
                            <Add style={{ fontSize: "14px" }} /> Create
                        </button>
                        {/* </Tooltip> */}
                    </div>
                </div>
                <div className="flex flex-col gap-3 text-sm">
                    {channel.distributions.length === 0 && (
                        <div className="italic text-gray-500 text-sm">No distributions connected</div>
                    )}
                    {channel.distributions.map((distribution, i) => (
                        <V2ChannelDistributionDetails
                            key={`distribution-${i}`}
                            channel={channel}
                            distribution={distribution}
                            channelId={channelId}
                            organizationId={organizationId}
                            engines={channel.engines}
                            onHoverEngine={(engineId: string) => onHightlightEngineId(engineId)}
                            onStopHoverEngine={() => {
                                setHighlightEngineId(undefined);
                                setHighlightIngestId(undefined);
                            }}
                        />
                    ))}
                </div>
            </div>

            <ConfirmDeleteDialog
                title="Delete channel"
                content={
                    <div>
                        Are you sure you want to delete the channel <strong>{channel.name}</strong> (
                        <span className="font-mono">{channel.id}</span>)? All connected engines, ingests and
                        distributions will be deleted as well.
                    </div>
                }
                open={openConfirmDelete}
                onClose={() => setOpenConfirmDelete(false)}
                onConfirm={() => onConfirmChannelDelete()}
            />
            <ConfirmDeleteDialog
                title="Start all engines"
                content={<div>Are you sure you want to start all engines?</div>}
                open={openConfirmStart}
                onClose={() => setOpenConfirmStart(false)}
                onConfirm={() => onConfirmChannelStart()}
            />
            <ConfirmDeleteDialog
                title="Stop engine"
                content={<div>Are you sure you want to stop all engines?</div>}
                open={openConfirmStop}
                onClose={() => setOpenConfirmStop(false)}
                onConfirm={() => onConfirmChannelStop()}
            />
            <UpdateChannelV2Dialog
                channel={channel}
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                organization={organization}
            />
            <CreateV2IngestDialog
                channelId={channelId}
                regions={channel.regions.filter((region) => region.rtmpPushEndpoint)}
                organizationId={organizationId}
                open={openCreateIngest}
                onClose={() => setOpenCreateIngest(false)}
            />
            {channel.ingests.length > 0 && (
                <CreateEngineDialog
                    abrLadders={channel.abrLadders.map((ladder) => ({ id: ladder.id, name: ladder.name }))}
                    channelId={channelId}
                    organizationId={organizationId}
                    ingests={channel.ingests.map((ing) => ({
                        id: ing.id,
                        name: ing.name,
                        region: ing.region === null ? undefined : ing.region,
                    }))}
                    onClose={() => setOpenCreateEngine(false)}
                    open={openCreateEngine}
                    regions={channel.regions.map((region) => ({ id: region.id, name: region.name }))}
                />
            )}
            {/* {channel.engines.length > 0 && ( */}
            <CreateDistributionDialog
                open={openCreateDistribution}
                onClose={() => setOpenCreateDistribution(false)}
                channelId={channelId}
                organizationId={organizationId}
                engines={channel.engines}
            />
            {/* )} */}

            <ToastContainer position="bottom-right" />
        </div>
    );
}

export const DELETE_V2_CHANNEL = gql`
    mutation DeleteV2ChannelMutation($input: DeleteV2ObjectInput!) {
        deleteV2Channel(input: $input)
    }
`;

export const DELETE_V2_ENGINE = gql`
    mutation DeleteV2EngineMutation($input: DeleteV2ObjectInput!) {
        deleteV2Engine(input: $input)
    }
`;

export const START_V2_ENGINE = gql`
    mutation StartV2EngineMutation($input: StartStopV2ObjectInput!) {
        startV2Engine(input: $input)
    }
`;

export const STOP_V2_ENGINE = gql`
    mutation StopV2EngineMutation($input: StartStopV2ObjectInput!) {
        stopV2Engine(input: $input)
    }
`;

export const START_V2_CHANNEL = gql`
    mutation StartV2ChannelMutation($input: StartStopV2ObjectInput!) {
        startV2Channel(input: $input)
    }
`;

export const STOP_V2_CHANNEL = gql`
    mutation StopV2ChannelMutation($input: StartStopV2ObjectInput!) {
        stopV2Channel(input: $input)
    }
`;

export const DELETE_V2_INGEST = gql`
    mutation DeleteV2IngestMutation($input: DeleteV2ObjectInput!) {
        deleteV2Ingest(input: $input)
    }
`;

export const DELETE_V2_DISTRIBUTION = gql`
    mutation DeleteV2DistributionMutation($input: DeleteV2ObjectInput!) {
        deleteV2Distribution(input: $input)
    }
`;

export const UPDATE_V2_CHANNEL = gql`
    mutation UpdateV2Channel($input: UpdateChannelV2Input!) {
        updateV2Channel(input: $input) {
            id
        }
    }
`;

export const CREATE_V2_CHANNEL = gql`
    mutation CreateV2Channel($input: CreateChannelV2Input!) {
        createV2Channel(input: $input) {
            id
        }
    }
`;

export const CREATE_V2_ENGINE = gql`
    mutation CreateV2Engine($input: CreateV2EngineInput!) {
        createV2Engine(input: $input) {
            id
        }
    }
`;

export const CREATE_V2_INGEST = gql`
    mutation CreateV2Ingest($input: CreateV2IngestInput!) {
        createV2Ingest(input: $input) {
            id
        }
    }
`;

export const CREATE_V2_DISTRIBUTION = gql`
    mutation CreateV2Distribution($input: CreateV2DistributionInput!) {
        createV2Distribution(input: $input) {
            id
        }
    }
`;
